import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateIcon } from '@radix-ui/react-icons';
import { AlertCircle, LoaderCircle, UserIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import useApiRequest from '../../../lib/hooks/useRequest';
import { SelfCheckoutDetailsResponseDto } from '../../../lib/interfaces/organisation';
import { Button } from '../../../ui/components/ui/button';
import DataFetchParent from '../../../ui/components/ui/data-fetch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/components/ui/dialog';
import { Form, FormControl, FormItem, FormLabel } from '../../../ui/components/ui/form';
import { Switch } from '../../../ui/components/ui/switch';
import { UpdateSelfCheckoutRequestDto } from '../../register/checkout/step2/types/request';
import { useIbanValidation } from '../hooks/use-iban-validation';
import { createBasePaymentDataSchema, createDataSchemaWithIbanValidation } from './forms/data-schemas/payment-data-schema';
import { personalDataSchema } from './forms/data-schemas/personal-data-schema';
import { selfCheckoutDataSchema } from './forms/data-schemas/self-checkout-data-schema';
import { InvoiceDataFormItems } from './forms/items/components/invoice-data-form-items';
import { StartDateSelector } from './forms/items/components/start-date-selector';
import PersonalFormItems from './forms/items/personal-form-items';
import SelfCheckoutFormItems from './self-checkout-form-items';

const RequestProcessor = ({
  id,
  setSelectedRequestId,
  setReload
}: {
  id: string;
  setSelectedRequestId: Dispatch<SetStateAction<string | undefined>>;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [item, setItem] = useState<SelfCheckoutDetailsResponseDto>();
  const [open, setOpen] = useState(id !== undefined);
  const [isAcceptedLoading, setIsAcceptedLoading] = useState(false);
  const [isRejectedLoading, setIsRejectedLoading] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const { apiRequest } = useApiRequest();

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<SelfCheckoutDetailsResponseDto>(`self-checkout/${id}`, 'GET');
    setItem(res.data);
    setIsLoading(false);
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [id, getItem]);

  useEffect(() => {
    setOpen(id !== undefined);
  }, [id]);

  const toggleAccept = async (type: 'accept' | 'decline') => {
    if (type === 'accept') {
      setIsAcceptedLoading(true);
    } else {
      setIsRejectedLoading(true);
    }
    await apiRequest(`self-checkout/${id}/${type}`, 'POST', {
      toast: {
        toastText: 'Anfrage erfolgreich bearbeitet'
      }
    });
    setSelectedRequestId(undefined);
    setSearchParams({});
    setOpen(false);
    setReload((prev) => !prev);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedRequestId(undefined);
          setSearchParams({});
        }
        setOpen(open);
      }}>
      <DialogContent className="sm:max-w-md min-w-[560px] py-4 px-6 gap-5">
        <DialogHeader className="mb-7">
          <DialogTitle className="flex items-center gap-3">
            <UserIcon />
            <p className="text-[25px] font-bold">Kundenanfrage</p>
          </DialogTitle>
        </DialogHeader>

        <DataFetchParent
          isLoading={isLoading}
          data={item}
          renderElement={(data: SelfCheckoutDetailsResponseDto) => (
            <div className="flex flex-col gap-4">
              <DialogFooter className="sm:justify-start flex gap-2">
                <Button
                  variant="destructive"
                  onClick={() => toggleAccept('decline')}
                  className="w-1/2 gap-2"
                  disabled={isRejectedLoading || isAcceptedLoading}>
                  {isRejectedLoading && <LoaderCircle className="animate-spin" />}
                  Ablehnen
                </Button>
                <Button
                  variant="brand"
                  onClick={() => toggleAccept('accept')}
                  className="w-1/2 gap-2"
                  disabled={isAcceptedLoading || isRejectedLoading}>
                  {isAcceptedLoading && <LoaderCircle className="animate-spin" />}
                  Akzeptieren
                </Button>
              </DialogFooter>
              <hr />
              <Forms data={data} id={id} />
            </div>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

const Forms = ({ data, id }: { data: SelfCheckoutDetailsResponseDto; id: string }) => {
  const { apiRequest } = useApiRequest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isGermany = data.country === 'DE';

  // Admin-Switch: bei DE-Kunden umschaltbar zwischen SEPA und Rechnung.
  // Initial aus dem Sentinel abgeleitet — wenn IBAN/BIC "KEIN EINTRAG", ist die Anfrage als Rechnung gespeichert.
  const [useInvoiceAdmin, setUseInvoiceAdmin] = useState(data.iban === 'KEIN EINTRAG' && data.bic === 'KEIN EINTRAG');
  // Auslandskunden sind zwangsweise Rechnung; Switch nur bei DE relevant.
  const effectiveUseInvoice = !isGermany || useInvoiceAdmin;

  const { validateIban, isValidating, ibanValid, bicValid } = useIbanValidation({
    apiRoute: 'self-checkout/admin/iban-validation',
    apiRequest
  });

  const basePaymentDataSchema = createBasePaymentDataSchema();
  const selfCheckoutRequestDataSchema = personalDataSchema.and(basePaymentDataSchema).and(selfCheckoutDataSchema);
  const selfCheckoutRequestDataSchemaWithIbanValidation = createDataSchemaWithIbanValidation(selfCheckoutRequestDataSchema, {
    validateIban,
    country: data.country,
    forceUseInvoice: effectiveUseInvoice
  });
  type SelfCheckoutRequestDataSchemaType = z.infer<typeof selfCheckoutRequestDataSchemaWithIbanValidation>;

  const getSelectedDate = () => {
    // get the nearest date to the newCustomerdate from the data.selectableEntities
    const newCustomerDate = new Date(data.newCustomerDate);
    const capacities = data.meta.capacities;

    const nearestDate = capacities.capacityBySlotsNeeded[0].capacity.find((capacity) => {
      const date = new Date(capacity.date);
      return date >= newCustomerDate;
    });

    if (nearestDate) {
      const foundDate = new Date(nearestDate.date);
      const timeDifference = foundDate.getTime() - newCustomerDate.getTime();
      const hoursFromNewCustomerDate = timeDifference / (1000 * 60 * 60);

      // Check if the nearest date is within 24 hours of the newCustomerDate
      if (hoursFromNewCustomerDate <= 3) {
        return nearestDate.date;
      }
    }

    return 'chooseDate ' + newCustomerDate.toISOString();
  };

  const form = useForm<SelfCheckoutRequestDataSchemaType>({
    resolver: zodResolver(selfCheckoutRequestDataSchemaWithIbanValidation),
    mode: 'onBlur',
    defaultValues: {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || '',
      name: data.name || '',
      country: data.country || '',
      vatId: data.vatId || '',
      street: data.street || '',
      houseNumber: data.houseNumber || '',
      postCode: data.postCode || '',
      city: data.city || '',
      invoiceMail: data.invoiceMail || '',
      bic: data.bic && data.bic !== 'KEIN EINTRAG' ? data.bic : '',
      iban: data.iban && data.iban !== 'KEIN EINTRAG' ? data.iban : '',
      allowedJobsCount: data.allowedJobsCount || 0,
      newCustomerDate: getSelectedDate(),
      packageName: data.packageName || '',
      packagePrice: data.packagePrice || 0,
      minimumContractDurationMonths: data.minimumContractDurationMonths || 3,
      earlyOptOut: data.earlyOptOut || false,
      //trialStartAt: data.trialStartAt ? new Date(data.trialStartAt).toISOString().split('T')[0] : undefined,
      additionalConditions: data.additionalConditions || null,
      additionalPriceConditions: data.additionalPriceConditions || null
    }
  });

  // Trigger validation when form values change, except for IBAN and BIC which are validated on blur
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      // Skip IBAN and BIC validation on change, they will be validated on blur
      if (name !== 'iban' && name !== 'bic') {
        form.trigger(name as keyof SelfCheckoutRequestDataSchemaType);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Bei Switch-Flip: Form-Validierung neu auslösen, damit IBAN/BIC-Errors verschwinden bzw. erscheinen
  useEffect(() => {
    form.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useInvoiceAdmin]);

  const handleInvoiceSwitch = (next: boolean) => {
    setUseInvoiceAdmin(next);
    if (next) {
      form.setValue('iban', '');
      form.setValue('bic', '');
    }
  };

  const onSubmit = async (data: SelfCheckoutRequestDataSchemaType) => {
    setIsSubmitting(true);

    // Combine data from both steps
    const requestData: UpdateSelfCheckoutRequestDto = {
      ...data,
      isInvoice: effectiveUseInvoice,
      invoiceMail: data.invoiceMail !== '' ? data.invoiceMail : undefined,
      // Bei Rechnung: IBAN/BIC weglassen — Backend setzt Sentinel via isInvoice-Flag
      bic: effectiveUseInvoice ? undefined : data.bic !== '' && data.bic !== 'KEIN EINTRAG' ? data.bic : undefined,
      iban: effectiveUseInvoice ? undefined : data.iban !== '' && data.iban !== 'KEIN EINTRAG' ? data.iban : undefined,
      trialStartAt: data.trialStartAt ? data.trialStartAt : undefined
    };

    // Make API request
    await apiRequest(`self-checkout/${id}`, 'PATCH', {
      body: requestData,
      toast: {
        toastText: 'Kundenanfrage erfolgreich bearbeitet'
      }
    });
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.log(errors))} className="flex flex-col gap-4">
          <p className="text-lg font-bold">Persönliche Daten:</p>
          <PersonalFormItems form={form} disableCountrySelection={true} />

          <hr />

          <p className="text-lg font-bold">Paketdaten:</p>
          <StartDateSelector form={form} capacities={data.meta.capacities} isAdmin={true} />
          <SelfCheckoutFormItems form={form} packages={data.meta.packages} />
          {/*<div className="flex flex-col gap-2">
            <FormLabel className="text-sm font-medium">Testphase</FormLabel>
            {data.trialStartAt ? (
              <FormField
                control={form.control}
                name="trialStartAt"
                render={({ field }) => (
                  <FormItem className="flex flex-col bg-white rounded-lg">
                    <FormControl>
                      <Input type="date" value={field.value || ''} onChange={(e) => field.onChange(e.target.value)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Keine Testphase gebucht</p>
            )}
          </div>*/}

          <hr />

          <p className="text-lg font-bold">Zahlungsdaten:</p>
          {isGermany && (
            <>
              <FormItem className="flex flex-row items-center gap-3 bg-white rounded-lg space-y-0">
                <FormLabel>Zahlung auf Rechnung</FormLabel>
                <FormControl>
                  <Switch checked={useInvoiceAdmin} onCheckedChange={handleInvoiceSwitch} />
                </FormControl>
              </FormItem>
              <div className="flex flex-row items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm text-muted-foreground">Achtung die Aktivierung der Rechnung führt zu einer Löschung der Kontodaten</span>
              </div>
            </>
          )}
          <InvoiceDataFormItems form={form} isValidating={isValidating} ibanValid={ibanValid} bicValid={bicValid} useInvoice={effectiveUseInvoice} />

          <hr />

          <div className="flex gap-2 flex-col">
            <Button type="submit" disabled={isSubmitting || !form.formState.isValid} className="self-end gap-2">
              <UpdateIcon className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default RequestProcessor;
